import type { RubricListItemDto } from '@sara/api-client';
import { Badge } from '@sara/ui/components/badge';
import { DataPanel } from '@sara/ui/components/data-panel';
import { Link, type LinkProps } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';

interface RubricListProps {
  rubrics: RubricListItemDto[];
  getLinkProps: (rubric: RubricListItemDto) => LinkProps;
}

interface RowProps {
  rubric: RubricListItemDto;
  linkProps: LinkProps;
}

function Row({ rubric, linkProps }: RowProps) {
  return (
    <Link
      {...linkProps}
      className="group grid grid-cols-[1fr_auto] items-center gap-6 px-6 py-5 transition-colors hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background"
    >
      <div className="min-w-0">
        <div className="flex items-baseline gap-3">
          <p className="text-[15px] font-medium tracking-tight text-foreground transition-colors group-hover:text-primary">
            {rubric.name}
          </p>
          <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
            v{rubric.latestVersion}
          </span>
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {rubric.dimensionCount} {rubric.dimensionCount === 1 ? 'dimension' : 'dimensions'}
          </span>
          {rubric.archivedAt ? (
            <Badge variant="outline" className="text-[10px]">
              Archived
            </Badge>
          ) : null}
        </div>
        {rubric.description && (
          <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">{rubric.description}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-6 text-xs text-muted-foreground">
        <span className="tabular-nums">
          {new Date(rubric.updatedAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
        <ArrowUpRight
          className="h-4 w-4 -translate-x-1 text-muted-foreground/60 transition-all group-hover:translate-x-0 group-hover:text-foreground"
          strokeWidth={1.5}
        />
      </div>
    </Link>
  );
}

export function RubricList({ rubrics, getLinkProps }: RubricListProps) {
  if (rubrics.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-20 text-center">
        <p className="font-display text-xl font-medium text-foreground">No rubrics yet.</p>
        <p className="mt-2 max-w-xs text-sm text-muted-foreground">
          Create your first rubric to score conversations.
        </p>
      </div>
    );
  }

  return (
    <DataPanel>
      <DataPanel.Header columns="1fr auto">
        <span>Name</span>
        <span>Updated</span>
      </DataPanel.Header>
      <DataPanel.Body>
        <ul className="divide-y divide-border/60">
          {rubrics.map((rubric) => (
            <li key={rubric.id}>
              <Row rubric={rubric} linkProps={getLinkProps(rubric)} />
            </li>
          ))}
        </ul>
      </DataPanel.Body>
      <DataPanel.Footer>
        <span className="tabular-nums">
          {rubrics.length} {rubrics.length === 1 ? 'rubric' : 'rubrics'}
        </span>
      </DataPanel.Footer>
    </DataPanel>
  );
}
